/**
 * Wishlist View Page (Paiseh A.dc design refresh)
 * View and manage a wishlist and its items
 */

import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useWishlist } from '@/hooks/useWishlist'
import { useItems } from '@/hooks/useItems'
import { ItemCard } from '@/components/items/ItemCard'
import { ItemForm } from '@/components/items/ItemForm'
import { Modal } from '@/components/common/Modal'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { LoginButton } from '@/components/auth/LoginButton'
import { WishlistItem, ItemFormData } from '@/types'
import { createItem, updateItem, deleteItem } from '@/services/item.service'
import { deleteWishlist } from '@/services/wishlist.service'
import { analyticsEvents } from '@/utils/analytics'
import { getOccasionTheme } from '@/utils/occasionTheme'
import toast from 'react-hot-toast'

export const WishlistView = () => {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { wishlist, loading: wishlistLoading } = useWishlist(id)
  const { items, itemsByCategory, loading: itemsLoading } = useItems(id)

  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<WishlistItem | null>(null)
  const [showShareModal, setShowShareModal] = useState(false)
  const [shareLink, setShareLink] = useState<string | null>(null)
  const [itemToDelete, setItemToDelete] = useState<WishlistItem | null>(null)
  const [isDeletingItem, setIsDeletingItem] = useState(false)
  const [showDeleteWishlistDialog, setShowDeleteWishlistDialog] = useState(false)
  const [isDeletingWishlist, setIsDeletingWishlist] = useState(false)

  const isOwner = user?.uid === wishlist?.ownerId

  // Track wishlist view
  useEffect(() => {
    if (wishlist && id) {
      analyticsEvents.wishlistViewed(id, isOwner)
    }
  }, [wishlist, id, isOwner])

  const handleAddItem = async (data: ItemFormData) => {
    if (!id) return
    try {
      const result = await createItem(id, data)
      analyticsEvents.itemAdded(id, result.id, !!data.url, !!data.price)
      toast.success(result.message)
      setIsAddItemModalOpen(false)
    } catch (error: any) {
      toast.error(error.message || 'Failed to add item')
      throw error
    }
  }

  const handleUpdateItem = async (data: ItemFormData) => {
    if (!id || !editingItem) return
    try {
      const result = await updateItem(id, editingItem.id, data)
      analyticsEvents.itemUpdated(id, editingItem.id)
      toast.success(result.message)
      setEditingItem(null)
    } catch (error: any) {
      toast.error(error.message || 'Failed to update item')
      throw error
    }
  }

  const handleDeleteItem = async () => {
    if (!id || !itemToDelete) return
    setIsDeletingItem(true)
    try {
      const result = await deleteItem(id, itemToDelete.id)
      analyticsEvents.itemDeleted(id, itemToDelete.id)
      toast.success(result.message)
      setItemToDelete(null)
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete item')
    } finally {
      setIsDeletingItem(false)
    }
  }

  const handleDeleteWishlist = async () => {
    if (!id || !wishlist) return
    setIsDeletingWishlist(true)
    try {
      analyticsEvents.wishlistDeleted(id, wishlist.itemCount || 0)
      const result = await deleteWishlist(id)
      toast.success(result.message)
      navigate('/wishlists')
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete wishlist')
      setShowDeleteWishlistDialog(false)
    } finally {
      setIsDeletingWishlist(false)
    }
  }

  const openShare = () => {
    const link = `https://paiseh.sg/w/${wishlist!.id}`
    setShareLink(link)
    setShowShareModal(true)
    analyticsEvents.wishlistShared(wishlist!.id, 'generate_link')
  }

  const copyShareLink = () => {
    const link = shareLink || wishlist?.shareLink
    if (link && id) {
      navigator.clipboard.writeText(link)
      analyticsEvents.shareButtonClicked('copy_link')
      toast.success('Link copied to clipboard!')
    }
  }

  // If not authenticated, show login prompt
  if (!user) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center font-jakarta">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-24 h-24 bg-paiseh-peach rounded-full flex items-center justify-center mx-auto mb-6 text-5xl">
            🎁
          </div>
          <h2 className="font-display font-extrabold text-3xl text-ink mb-4">Wishlist</h2>
          <p className="text-paiseh-muted mb-8">Sign in to view and manage your wishlists</p>
          <div className="flex justify-center">
            <LoginButton />
          </div>
        </div>
      </div>
    )
  }

  if (wishlistLoading || itemsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream font-jakarta">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-paiseh-orange mx-auto mb-4"></div>
          <p className="text-paiseh-muted">Loading wishlist...</p>
        </div>
      </div>
    )
  }

  if (!wishlist) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream font-jakarta">
        <div className="text-center">
          <h2 className="font-display font-extrabold text-2xl text-ink mb-2">Wishlist not found</h2>
          <Link to="/wishlists" className="text-paiseh-orange font-bold hover:underline">
            Back to My Wishlists
          </Link>
        </div>
      </div>
    )
  }

  const theme = getOccasionTheme(wishlist.occasion, wishlist.emoji)
  const isShared = wishlist.visibility === 'shared'
  const categories = wishlist.categories || []
  const uncategorizedItems = itemsByCategory['uncategorized'] || []

  return (
    <div className="min-h-screen bg-cream text-ink font-jakarta">
      <main className="max-w-[1180px] mx-auto px-5 sm:px-10 py-14">
        {/* Wishlist header card */}
        <div className="bg-white rounded-[28px] border border-ink/[.06] shadow-[0_20px_50px_rgba(43,36,32,.08)] overflow-hidden mb-6">
          <div className="px-6 sm:px-10 pt-9 pb-7 border-b border-ink/[.07]">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex gap-5 items-center">
                <div
                  className="w-[70px] h-[70px] rounded-full text-white flex items-center justify-center text-3xl flex-none"
                  style={{ background: theme.circle }}
                >
                  {theme.emoji}
                </div>
                <div>
                  <div
                    className="text-[13px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: theme.tag }}
                  >
                    {wishlist.occasion || 'Wishlist'}
                  </div>
                  <h1 className="font-display font-extrabold text-[28px] sm:text-4xl mt-1">
                    {wishlist.title}
                  </h1>
                  {wishlist.description && (
                    <p className="text-paiseh-muted mt-2 max-w-[520px]">{wishlist.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-sm text-paiseh-faint font-semibold">
                    <span>🎁 {items.length} items</span>
                    {wishlist.reservedCount > 0 && (
                      <span className="text-paiseh-olive">✓ {wishlist.reservedCount} reserved</span>
                    )}
                  </div>
                </div>
              </div>

              {isOwner && (
                <div className="flex gap-2.5">
                  <button
                    onClick={openShare}
                    className="bg-white border border-ink/[.15] text-ink font-bold text-sm px-5 py-2.5 rounded-full hover:border-paiseh-orange transition-colors"
                  >
                    Share ↗
                  </button>
                  <button
                    onClick={() => setShowDeleteWishlistDialog(true)}
                    className="bg-white border border-ink/[.12] text-paiseh-faint font-bold text-sm px-5 py-2.5 rounded-full hover:text-red-600 hover:border-red-300 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>

            {isShared && (
              <div className="mt-5 bg-paiseh-peach rounded-[16px] px-5 py-4 flex gap-3 items-center text-[14.5px] text-[#9C4226] font-semibold">
                <span className="text-xl">🤫</span> This list is shared — friends can reserve gifts
                without you ever seeing who claimed what.
              </div>
            )}
          </div>

          {/* Items */}
          <div className="px-6 sm:px-10 py-7">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-extrabold text-2xl">Items</h2>
              {isOwner && items.length > 0 && (
                <button
                  onClick={() => setIsAddItemModalOpen(true)}
                  className="bg-paiseh-orange text-white font-bold text-sm px-5 py-2.5 rounded-full shadow-[0_8px_18px_rgba(240,98,62,.3)] hover:opacity-95 transition-opacity"
                >
                  + Add item
                </button>
              )}
            </div>

            {items.length === 0 ? (
              <EmptyWishlist isOwner={isOwner} onAdd={() => setIsAddItemModalOpen(true)} />
            ) : (
              <div className="space-y-8">
                {categories.map((category) => {
                  const categoryItems = itemsByCategory[category.id] || []
                  if (categoryItems.length === 0) return null
                  return (
                    <div key={category.id}>
                      <div
                        className="flex items-center gap-2 mb-4 pl-3"
                        style={{ borderLeftColor: category.color, borderLeftWidth: 4 }}
                      >
                        <h3 className="font-display font-bold text-lg">{category.name}</h3>
                        <span className="text-sm text-paiseh-faint">({categoryItems.length})</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {categoryItems.map((item) => (
                          <ItemCard
                            key={item.id}
                            item={item}
                            isOwner={isOwner}
                            variant="grid"
                            onEdit={setEditingItem}
                            onDelete={(item) => setItemToDelete(item)}
                          />
                        ))}
                      </div>
                    </div>
                  )
                })}

                {uncategorizedItems.length > 0 && (
                  <div>
                    {categories.length > 0 && (
                      <h3 className="font-display font-bold text-lg mb-4">
                        Uncategorized ({uncategorizedItems.length})
                      </h3>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {uncategorizedItems.map((item) => (
                        <ItemCard
                          key={item.id}
                          item={item}
                          isOwner={isOwner}
                          variant="grid"
                          onEdit={setEditingItem}
                          onDelete={(item) => setItemToDelete(item)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Add Item Modal */}
      <Modal isOpen={isAddItemModalOpen} onClose={() => setIsAddItemModalOpen(false)} title="Add an item">
        <ItemForm
          categories={categories}
          onSubmit={handleAddItem}
          onCancel={() => setIsAddItemModalOpen(false)}
          submitLabel="Add to wishlist"
        />
      </Modal>

      {/* Edit Item Modal */}
      <Modal isOpen={!!editingItem} onClose={() => setEditingItem(null)} title="Edit item">
        {editingItem && (
          <ItemForm
            initialData={{
              title: editingItem.title,
              description: editingItem.description,
              url: editingItem.url || undefined,
              imageUrl: editingItem.imageUrl || undefined,
              price: editingItem.price || undefined,
              currency: editingItem.currency,
              categoryId: editingItem.categoryId || undefined,
              priority: editingItem.priority,
            }}
            categories={categories}
            onSubmit={handleUpdateItem}
            onCancel={() => setEditingItem(null)}
            submitLabel="Save changes"
          />
        )}
      </Modal>

      {/* Share Modal */}
      <Modal isOpen={showShareModal} onClose={() => setShowShareModal(false)} title="Share Wishlist">
        <div className="space-y-4 font-jakarta">
          <p className="text-paiseh-muted">
            Anyone with this link can view your wishlist and reserve items.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={shareLink || wishlist.shareLink || ''}
              className="flex-1 px-4 py-2.5 border border-ink/[.15] rounded-full bg-[#FDFBF7] text-sm"
            />
            <button
              onClick={copyShareLink}
              className="bg-paiseh-orange text-white font-bold text-sm px-6 py-2.5 rounded-full hover:opacity-95 transition-opacity"
            >
              Copy
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Item Dialog */}
      <ConfirmDialog
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={handleDeleteItem}
        title="Delete Item"
        message={`Are you sure you want to delete "${itemToDelete?.title}"? This action cannot be undone.`}
        variant="danger"
        confirmText="Delete Item"
        cancelText="Cancel"
        isLoading={isDeletingItem}
      />

      {/* Delete Wishlist Dialog */}
      <ConfirmDialog
        isOpen={showDeleteWishlistDialog}
        onClose={() => setShowDeleteWishlistDialog(false)}
        onConfirm={handleDeleteWishlist}
        title="Delete Wishlist"
        message={`Are you sure you want to delete "${wishlist?.title}" and all its items? This action cannot be undone.`}
        variant="danger"
        confirmText="Delete Wishlist"
        cancelText="Cancel"
        isLoading={isDeletingWishlist}
      />
    </div>
  )
}

/** Empty wishlist state from the design — stacked dashed cards + quick add */
const EmptyWishlist = ({ isOwner, onAdd }: { isOwner: boolean; onAdd: () => void }) => (
  <div className="py-14 text-center">
    <div className="relative w-[150px] h-[130px] mx-auto mb-1.5">
      <div className="absolute top-3.5 left-5 w-[110px] h-[88px] bg-[#FDFBF7] border-2 border-dashed border-[#E0D3C0] rounded-[18px] -rotate-[7deg]" />
      <div className="absolute top-2 left-[34px] w-[110px] h-[88px] bg-[#FDFBF7] border-2 border-dashed border-[#E0D3C0] rounded-[18px] rotate-[5deg]" />
      <div className="absolute top-[22px] left-10 w-16 h-16 rounded-[20px] bg-paiseh-peach flex items-center justify-center text-[32px] rotate-[3deg]">
        🎁
      </div>
    </div>
    <h3 className="font-display font-extrabold text-[24px] sm:text-[26px] mt-5 mb-2">
      Your wishlist is empty — for now!
    </h3>
    <p className="text-base leading-[1.55] text-paiseh-muted max-w-[400px] mx-auto mb-7">
      {isOwner
        ? 'Add the first thing you’re wishing for. Paste a link from any store and fill in the photo, name, and price.'
        : 'Nothing here yet — check back soon!'}
    </p>
    {isOwner && (
      <>
        <button
          onClick={onAdd}
          className="max-w-[520px] w-full mx-auto flex gap-2.5 bg-[#FDFBF7] border border-ink/[.1] rounded-full p-[7px_7px_7px_20px] items-center hover:border-paiseh-orange transition-colors"
        >
          <span className="text-base text-[#B0A392]">🔗</span>
          <span className="flex-1 text-left text-[15px] text-[#B0A392]">Paste a product link…</span>
          <span className="bg-paiseh-orange text-white font-bold text-[15px] px-6 py-3 rounded-full">
            Add item
          </span>
        </button>
        <div className="text-[13px] text-paiseh-faint font-semibold mt-3.5">or</div>
        <button
          onClick={onAdd}
          className="mt-3.5 inline-flex bg-white border border-ink/[.12] text-ink font-bold text-sm px-5 py-2.5 rounded-full hover:border-paiseh-orange transition-colors"
        >
          ✍️ Add manually
        </button>
      </>
    )}
  </div>
)
